import { useState, useMemo, useEffect, useCallback } from "react";
import { InboxIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "shared/components";
import type { BaseOption } from "shared/components";
import { useMailboxes } from "../../settings-communication/api/hooks";
import { useCrmContact } from "../api/crm";
import { useSendOptions } from "../api/hooks";
import { LeadType } from "views/leads-id/types";
import { usePropertyDefaultEmail } from "../../properties-id/lib/propertyDefaultEmail";
import { nylasApi, AVAILABLE_MAIL_GRANTS_QUERY_KEY } from "api/nylas";
import type { Mailbox } from "../../settings-communication/api/types";
import { getEmailIcon } from "../utils/emailProviderIcon";
import { RoutingFieldsRow } from "./RoutingFieldsRow";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { cx } from "shared/utils/classnames";
import { PARTICIPANT_FIELD_STYLES } from "./participantFieldStyles";

interface EmailRoutingSelectProps {
  contactId?: string;
  conversationId?: string;
  onSelectionChange?: (mailboxId: string, toEmail: string) => void;
  menuPlacement?: "top" | "bottom" | "auto";
  /**
   * Pop-up participants block (CRM-1133 design 2152:16771): labeled boxed
   * fields — stacked on the floating card, side by side in the drawer —
   * instead of the inline "From: … → To: …" row.
   */
  compact?: boolean;
}

const DEFAULT_EMAIL = import.meta.env.VITE_APP_DEFAULT_EMAIL;

const createEmailOption = (
  email: string,
  subtitle?: string,
  provider?: string
) => {
  const icon = getEmailIcon(email, provider);

  return (
    <div className="flex items-center gap-2 h-8">
      {icon ? (
        <img src={icon} alt="" className="w-4 h-4 flex-shrink-0" />
      ) : (
        <HugeiconsIcon
          icon={InboxIcon}
          size={16}
          className="text-content-300 flex-shrink-0"
        />
      )}
      <div>
        <div className="text-sm font-medium text-content-1000">{email}</div>
        {subtitle ? (
          <div className="text-xs font-medium text-content-300">{subtitle}</div>
        ) : null}
      </div>
    </div>
  );
};

const createGroupLabel = (label: string): BaseOption => ({
  value: `__group_${label}`,
  label: (
    <div className="px-2 h-6 flex items-center text-sm text-content-400 tracking-wide cursor-default">
      {label}
    </div>
  ),
  isDisabled: true
});

const useAvailableMailGrants = () =>
  useQuery({
    queryKey: [...AVAILABLE_MAIL_GRANTS_QUERY_KEY],
    queryFn: () => nylasApi.listAvailableMailGrants()
  });

export const EmailRoutingSelect = ({
  contactId,
  conversationId,
  onSelectionChange,
  menuPlacement = "top",
  compact = false
}: EmailRoutingSelectProps) => {
  const isDrawerEmbed = useInboxEmbed()?.layout === "drawer";
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedToEmail, setSelectedToEmail] = useState<string | null>(null);

  const { data: contact } = useCrmContact(contactId ?? "");
  const { data: sendOptions } = useSendOptions(conversationId);

  // null → no restriction (unrestricted user, or options not loaded yet).
  const allowedToEmails = useMemo(() => {
    if (!sendOptions || sendOptions.unrestricted) return null;
    return new Set(sendOptions.emails.map((email) => email.toLowerCase()));
  }, [sendOptions]);
  const { data: mailboxes } = useMailboxes();
  const { data: availableGrants = [] } = useAvailableMailGrants();

  // Owner leads carry no property, so they're excluded from this count.
  const singlePropertyId = useMemo(() => {
    const propertyIds = new Set<number>();
    (contact?.leads ?? []).forEach((lead) => {
      if (lead.lead_type === LeadType.Owner) return;
      if (lead.property_id != null) propertyIds.add(lead.property_id);
    });
    (contact?.applicants ?? []).forEach((applicant) => {
      const propertyId = applicant.application?.property?.id;
      if (propertyId != null) propertyIds.add(propertyId);
    });
    return propertyIds.size === 1 ? [...propertyIds][0] : undefined;
  }, [contact]);

  const { data: propertyEmailDefault } = usePropertyDefaultEmail(
    singlePropertyId ?? 0,
    singlePropertyId !== undefined
  );
  const propertyDefaultMailboxId = propertyEmailDefault?.default_mailbox_id;

  // Build a lookup: email → account_type from access-controlled grant list
  const grantByEmail = useMemo(
    () =>
      availableGrants.reduce((map, grant) => {
        map.set(grant.email, grant.account_type);
        return map;
      }, new Map<string, "personal" | "shared">()),
    [availableGrants]
  );

  const { mailboxOptions, defaultMailboxValue } = useMemo(() => {
    const active = (mailboxes ?? []).filter(
      (mb: Mailbox) =>
        mb.status === "active" && grantByEmail.has(mb.email_address)
    );

    const defaultMb = active.find((mb) => mb.is_default);
    const personalMbs = active.filter(
      (mb) => grantByEmail.get(mb.email_address) === "personal"
    );
    const sharedMbs = active.filter(
      (mb) => grantByEmail.get(mb.email_address) === "shared"
    );

    const showDefaultAsPersonal =
      !!DEFAULT_EMAIL &&
      !personalMbs.some((mb) => mb.email_address === DEFAULT_EMAIL) &&
      !sharedMbs.some((mb) => mb.email_address === DEFAULT_EMAIL);

    const options: BaseOption[] = [];
    let defaultValue = "";

    if (personalMbs.length > 0 || showDefaultAsPersonal) {
      options.push(createGroupLabel("Personal emails"));

      if (showDefaultAsPersonal) {
        const systemIsDefault = !defaultMb;
        if (systemIsDefault) defaultValue = "default";
        options.push({
          value: "default",
          label: createEmailOption(
            DEFAULT_EMAIL,
            systemIsDefault ? "Default" : undefined
          )
        });
      }

      personalMbs.forEach((mb) => {
        const isDefault = mb.id === defaultMb?.id;
        if (!defaultValue && isDefault) defaultValue = mb.id;
        options.push({
          value: mb.id,
          label: createEmailOption(
            mb.email_address,
            isDefault ? "Default" : undefined,
            mb.provider
          )
        });
      });
    }

    if (sharedMbs.length > 0) {
      options.push(createGroupLabel("Shared emails"));
      sharedMbs.forEach((mb) => {
        const isDefault = mb.id === defaultMb?.id;
        if (!defaultValue && isDefault) defaultValue = mb.id;
        options.push({
          value: mb.id,
          label: createEmailOption(
            mb.email_address,
            isDefault ? "Default" : undefined,
            mb.provider
          )
        });
      });
    }

    if (!defaultValue)
      defaultValue =
        options.find((o) => !o.isDisabled)?.value?.toString() ?? "";

    return { mailboxOptions: options, defaultMailboxValue: defaultValue };
  }, [mailboxes, grantByEmail]);

  const toEmailOptions = useMemo((): BaseOption[] => {
    if (!contact) return [];
    const contactName = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const visibleEmails = allowedToEmails
      ? contact.emails.filter((email) =>
          allowedToEmails.has(email.toLowerCase())
        )
      : contact.emails;

    if (visibleEmails.length > 0)
      return visibleEmails.map(
        (email): BaseOption => ({
          value: email,
          label: createEmailOption(email, contactName)
        })
      );

    // Restricted users get no system-default fallback — only addresses they
    // may reach.
    if (DEFAULT_EMAIL && !allowedToEmails)
      return [
        {
          value: DEFAULT_EMAIL,
          label: createEmailOption(DEFAULT_EMAIL, contactName)
        }
      ];

    return [];
  }, [contact, allowedToEmails]);

  const effectiveMailbox = useMemo(() => {
    if (
      selectedMailbox &&
      mailboxOptions.some((opt) => opt.value === selectedMailbox)
    )
      return selectedMailbox;
    if (
      propertyDefaultMailboxId &&
      mailboxOptions.some((opt) => opt.value === propertyDefaultMailboxId)
    )
      return propertyDefaultMailboxId;
    return defaultMailboxValue;
  }, [
    selectedMailbox,
    mailboxOptions,
    defaultMailboxValue,
    propertyDefaultMailboxId
  ]);

  const effectiveToEmail = useMemo(() => {
    if (
      selectedToEmail &&
      toEmailOptions.some((opt) => opt.value === selectedToEmail)
    )
      return selectedToEmail;
    const firstValue = toEmailOptions[0]?.value;
    return typeof firstValue === "string" ? firstValue : "";
  }, [selectedToEmail, toEmailOptions]);

  const handleSelectionChange = useCallback(() => {
    if (effectiveMailbox && effectiveToEmail && onSelectionChange)
      onSelectionChange(effectiveMailbox, effectiveToEmail);
  }, [effectiveMailbox, effectiveToEmail, onSelectionChange]);

  useEffect(() => {
    handleSelectionChange();
  }, [handleSelectionChange]);

  const selectableOptions = mailboxOptions.filter((o) => !o.isDisabled);
  if (selectableOptions.length === 0 || toEmailOptions.length === 0)
    return null;

  if (compact)
    return (
      <div
        className={cx(
          "flex w-full min-w-0",
          isDrawerEmbed ? "flex-row gap-2" : "flex-col gap-2"
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-content-200">From</span>
          <Select
            id="from-mailbox"
            hideOptional
            searchable={false}
            value={effectiveMailbox}
            onChange={(e) => setSelectedMailbox(e.target.value)}
            options={mailboxOptions}
            className="!min-w-0 !font-medium"
            styles={PARTICIPANT_FIELD_STYLES}
            legacyErrorMargins={false}
            isDisabled={selectableOptions.length === 0}
            menuPlacement={menuPlacement}
            placeholder="Select..."
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-content-200">To</span>
          <Select
            id="to-email"
            hideOptional
            searchable={false}
            value={effectiveToEmail}
            onChange={(e) => setSelectedToEmail(e.target.value)}
            options={toEmailOptions}
            className="!min-w-0 !font-medium"
            styles={PARTICIPANT_FIELD_STYLES}
            legacyErrorMargins={false}
            isDisabled={toEmailOptions.length === 0}
            menuPlacement={menuPlacement}
            placeholder="Select..."
          />
        </div>
      </div>
    );

  return (
    <RoutingFieldsRow
      from={
        <Select
          id="from-mailbox"
          hideOptional
          searchable={false}
          value={effectiveMailbox}
          onChange={(e) => setSelectedMailbox(e.target.value)}
          options={mailboxOptions}
          className="!border-0 !shadow-none !bg-transparent !text-content-1000 !font-medium !p-0"
          legacyErrorMargins={false}
          isDisabled={selectableOptions.length === 0}
          menuPlacement={menuPlacement}
          placeholder={
            selectableOptions.length === 0
              ? "No mailboxes available"
              : "Select..."
          }
        />
      }
      to={
        <Select
          id="to-email"
          hideOptional
          searchable={false}
          value={effectiveToEmail}
          onChange={(e) => setSelectedToEmail(e.target.value)}
          options={toEmailOptions}
          className="!border-0 !shadow-none !bg-transparent !text-content-1000 !font-medium !p-0"
          legacyErrorMargins={false}
          isDisabled={toEmailOptions.length === 0}
          menuPlacement={menuPlacement}
          placeholder={
            toEmailOptions.length === 0 ? "No contact emails" : "Select..."
          }
        />
      }
    />
  );
};
